import type { CategoryPrompt } from "../types";

import React from "react";

import {
  BedDouble,
  Frame,
  Gift,
  Home,
  Image,
  Leaf,
  Palette,
  Thermometer,
} from "lucide-react";

export const homeDecorPrompts: CategoryPrompt[] = [
  {
    icon: <Leaf />,
    title: "Seasonal Collection Landing Pages",
    description:
      "Build a seasonal page with color-coordinated collections and a gallery for styled room photos.",
    prompt: `**Build a seasonal collection landing page that showcases coordinated products and styled inspiration for timely home refreshes.**

**SEASONAL FOCUS:** e.g., "Spring Refresh Collection" OR "Cozy Fall Essentials" OR "Summer Coastal Vibes" OR "Winter Warm-Up Collection"

**COLLECTION THEME:** e.g., Color palette, material focus, mood/vibe, seasonal functionality

**Shopper Type:** Choose one:
- [ ] Seasonal decorators (refresh decor regularly)
- [ ] Trend followers (want current looks)
- [ ] Gift shoppers (seasonal entertaining, hosting)

**Tone & Style:**
- Seasonal excitement and freshness
- Trend-aware but timeless
- Inspiring and aspirational
- "New season, new style" energy

**CTA Guidelines:** Each CTA should:
- Emphasize seasonality ("Get the Spring Look")
- Use collection language ("Shop Full Collection")
- Reference timeliness ("Perfect for Now")
- Include coordination ("Designed to Mix")

**Conversion Tactics Use:**
- Color-coordinated product groupings
- Complete room look bundles
- Seasonal urgency (limited-time)
- Styled room inspiration photos
- Mix-and-match guidance
- Early access or limited quantities

**Key Elements to Include:**
- **Hero Section:** Seasonal headline, styled hero image, color palette display, "Shop Collection" CTA
- **Seasonal Story:** Why this collection now, mood/inspiration (2-3 paragraphs)
- **Color Palette:** 4-6 coordinating colors featured throughout collection
- **Product Collections by Category:**
  - Textiles (pillows, throws, rugs)
  - Decor accents (vases, candles, art)
  - Furniture pieces (seasonal chairs, tables)
  - Lighting (seasonal fixtures)
- **Coordinated Product Groups:** Items designed to work together with visual groupings
- **Complete Look Bundles:** Pre-styled room sets with "Get This Look" savings
- **Styled Room Gallery (6-10):** Professional photos showing collection in situ
- **Mix & Match Guide:** How to combine pieces across collection
- **Seasonal Features:** What makes these items perfect for this season
- **Color Story Explanation:** Why these colors for this time of year
- **Material Focus:** Seasonal textures (linen for spring, velvet for fall)
- **Trend Integration:** Current design trends reflected in collection
- **Room-by-Room Suggestions:** Collection pieces for living room, bedroom, dining
- **Budget Tiers:** Refresh options at entry, mid, and investment levels
- **Limited Availability:** Seasonal nature, may not restock
- **Customer Styled Photos:** Real homes featuring collection pieces
- **Early Access Perk:** "Shop before general release" if applicable
- **Gift Suggestions:** Seasonal hosting/entertaining items
- **CTAs:** Hero, with bundles, styled galleries, categories (5-6 total)
- **FAQ:** 4-5 questions (Restock? Available year-round? Mix with existing? Returns?)

**Goal:** Maximize seasonal revenue by creating timely urgency around coordinated collections that make refreshing home decor easy through pre-styled bundles, color coordination guidance, and inspirational room galleries that increase basket size and convert seasonal decorating enthusiasm into immediate purchases.`,
  },
  {
    icon: <Thermometer />,
    title: "Design Mistake Listicle Advertorials",
    description:
      "Build a listicle addressing common design mistakes, showcasing my products as solutions with styling tips.",
    prompt: `**Build a design mistake listicle that identifies common styling errors and positions your products as the elegant solutions with expert guidance.**

**TOPIC:** e.g., "7 Design Mistakes Making Your Home Look Cheap" OR "5 Decor Errors Ruining Your Space" OR "Why Your Room Feels Off (And How to Fix It)"

**PRODUCTS FEATURED:** e.g., "Our area rug solves mistake #3" OR "Our lighting fixes issue #5"

**Reader Type:** Choose one:
- [ ] Design-frustrated (know something's wrong, can't identify)
- [ ] Style-conscious (want elevated look)
- [ ] DIY decorators (learning design principles)

**Tone & Style:**
- Friendly design mentor not critical
- Educational and empowering
- "Everyone makes these mistakes"
- Solutions-focused positivity

**CTA Guidelines:** Each CTA should:
- Reference problem solved ("Fix This Now")
- Use transformation language ("Elevate Your Space")
- Include design principle ("Proper Scale")
- Emphasize impact ("Instant Upgrade")

**Conversion Tactics Use:**
- Before/after mistake corrections
- Design principle explanations
- Professional stylist validation
- Scale/proportion guides
- Customer transformation photos
- Complete look bundles

**Key Elements to Include:**
- **Hook:** Relatable design frustration (2-3 paragraphs on "something feels off")
- **List Items (6-8 mistakes):** Numbered error → design principle → your solution
- **Mistake Structure Per Item:**
  - Error headline: "Mistake #X: [Common design mistake]"
  - Why this is wrong: Design principle violated (scale, proportion, balance)
  - Visual impact: How it makes space feel (smaller, cheaper, cluttered)
  - The solution: Your product that corrects it
  - Design principle applied: How your product fixes the mistake
  - Styling guidance: Proper placement, scale, coordination
  - Expert tip: Designer insight on this principle
  - Customer photo: Space transformed by fixing this mistake
- **Product Callouts:** Design features highlighted with "Shop Solution" CTA
- **Before/After Comparisons:** Mistake vs. corrected spaces (2-3 throughout)
- **Design Principles Guide:** Scale, proportion, balance, focal points explained
- **Professional Validation:** Interior designer quotes on these mistakes
- **Measurement Guidance:** How to choose right size/scale
- **Multi-Mistake Rooms:** Complete transformations fixing several issues
- **CTAs:** After mistake #2, after mistake #5, conclusion (5-7 total)
- **FAQ:** 4-5 questions (Right size for my space? Works with existing? Returns? Budget options?)

**Goal:** Convert design-frustrated shoppers into confident buyers by validating common mistakes, teaching foundational design principles, and demonstrating how your thoughtfully-designed products provide elegant solutions that transform spaces from amateur to polished with proper scale, proportion, and styling guidance.`,
  },
  {
    icon: <Image />,
    title: "Shoppable Lookbook Pages",
    description:
      "Build a lookbook page with styled room photos, numbered product callouts, and 'Add to Cart' CTAs for products used in the photos.",
    prompt: `**Build a shoppable lookbook page with styled room photography featuring numbered product callouts and instant shopping integration.**

**LOOKBOOK THEME:** e.g., "Modern Minimalist Lookbook" OR "Coastal Living Collection" OR "Warm Autumn Interiors" OR "Small Space Solutions"

**AESTHETIC FOCUS:** e.g., Style-specific, seasonal, trend-driven, room-focused

**Visitor Type:** Choose one:
- [ ] Visual shoppers (need to see styled)
- [ ] Look-stealers (want complete aesthetic)
- [ ] Inspiration browsers (shopping via discovery)

**Tone & Style:**
- Magazine editorial energy
- Aspirational but achievable
- Minimal text, maximum visual
- "Get this look" simplicity

**CTA Guidelines:** Each CTA should:
- Be direct and simple ("Shop", "Add", "Get")
- Reference complete look ("Shop This Room")
- Use numbered system ("Shop Item #3")
- Emphasize ease ("One-Click Add")

**Conversion Tactics Use:**
- Numbered product overlays on images
- "Shop the look" complete room bundles
- Quick-add without leaving page
- Price display on hover
- Multiple room/angle variations
- Bundle discount for complete looks

**Key Elements to Include:**
- **Hero Section:** Lookbook title, stunning styled image, "Shop Looks" CTA
- **Lookbook Introduction:** Theme/aesthetic statement (1-2 paragraphs)
- **Styled Room Photos (8-15):**
  - High-quality professionally styled image
  - Numbered callouts on each product (1, 2, 3, etc.)
  - Hover reveals product name + price
  - Click goes to product or quick-add
- **Product Detail Sidebar/Footer per Image:**
  - List of all numbered products
  - Product name, price, quick description
  - Individual "Add to Cart" buttons
  - "Shop Complete Look" bundle option
- **Room Angles:** Multiple views of same space showing different products
- **Quick Add Functionality:** Add items without leaving lookbook
- **Bundle Options:** Pre-selected room sets with savings
- **Color/Material Variations:** Alternative options for key pieces
- **Styling Notes:** Brief tips on achieving the look
- **Price Points Per Look:** Total room cost displayed
- **Mix & Match:** How to combine items across different looks
- **Customer Recreation Gallery:** Real homes recreating lookbook styles (4-6 photos)
- **Budget Alternatives:** "Get the look for less" options
- **Room Dimensions:** Space size reference for scale
- **Shopping Cart Preview:** Running total as items added
- **Save/Share Options:** Wishlist or share looks
- **CTAs:** Hero, on each product callout, bundle sections (numerous throughout)
- **FAQ:** 3-4 questions (Buy separately? Bundle savings? Substitutions? Dimensions?)

**Goal:** Convert visual shoppers into buyers by removing friction between inspiration and purchase through seamless shoppable integration that lets customers instantly add individual items or complete styled looks to cart, maximizing basket size through aspirational room styling and convenient bundle options.`,
  },
  {
    icon: <Frame />,
    title: "Use-Case Specific Landing Page",
    description:
      "Create a page targeting a specific use case (e.g., rental-friendly) with solutions and product recommendations for that scenario.",
    prompt: `**Build a use-case specific landing page that addresses unique constraints and positions your products as purpose-built solutions for that scenario.**

**USE CASE:** e.g., "Rental-Friendly Decor" OR "Pet-Proof Home" OR "Kid-Safe Spaces" OR "Frequent Movers" OR "Temporary Housing"

**CONSTRAINT FOCUS:** e.g., No permanent damage, easy mobility, durability needs, temporary solutions

**Target Customer:** Choose one:
- [ ] Constraint-bound (must work within limitations)
- [ ] Damage-anxious (security deposits, pet/kid concerns)
- [ ] Lifestyle-specific (frequent moves, temporary)

**Tone & Style:**
- Understanding and validating
- Solution-focused not limiting
- "You can still have style" empowerment
- Practical problem-solver

**CTA Guidelines:** Each CTA should:
- Reference constraint solution ("No Damage Needed")
- Use scenario language ("Perfect for Renters")
- Include key benefit ("Easy to Remove")
- Emphasize suitability ("Made for This")

**Conversion Tactics Use:**
- Constraint validation messaging
- Specific feature callouts (removable, portable, washable)
- Scenario testimonials
- Installation/removal demonstrations
- Damage-free guarantees
- Bundle for complete scenario solution

**Key Elements to Include:**
- **Hero Section:** Use case headline, hero image, primary constraint benefit, "Shop Solutions" CTA
- **The Challenge:** Why this scenario needs specific solutions (2-3 paragraphs on constraints)
- **Key Requirements:** What products must do for this use case (removable, durable, portable, etc.)
- **Product Solutions (8-15):** Items specifically addressing scenario needs
- **Feature Callouts Per Product:**
  - How it meets constraint (damage-free install, washable, lightweight)
  - Installation/removal method
  - Durability for scenario
  - Portability or reusability
- **Constraint-Specific Benefits:**
  - "No wall damage - command strips only"
  - "Machine washable covers"
  - "Lightweight and portable"
  - "Reversible design"
  - "Easy assembly/disassembly"
- **Installation/Removal Guides:** How-to videos or step-by-steps
- **Customer Success Stories (5-8):** Real people in same scenario
- **Before/After:** Transformations within constraints
- **Complete Room Solutions:** Bundles for entire spaces within use case
- **Damage-Free Guarantee:** If applicable for rentals
- **Portability Features:** Weight, size, assembly specs
- **Scenario-Specific Tips:** "Landlord-approved styling" or "Moving day advice"
- **Alternative Solutions:** Different approaches for same constraints
- **Budget Options:** Essential vs. complete scenario kits
- **Constraint FAQs Addressed:** Deposit safety, landlord approval, durability
- **CTAs:** Hero, with products, scenario bundles, after testimonials (5-6 total)
- **FAQ:** 6-8 questions (Really no damage? Landlord-approved? Reusable? Deposit-safe? Durable enough? Move-friendly?)

**Goal:** Convert constraint-limited shoppers into confident buyers by validating their unique scenario challenges and proving your products specifically solve their needs without compromise, backed by real testimonials from similar situations that demonstrate style and functionality within restrictions.`,
  },
  {
    icon: <BedDouble />,
    title: "Home Style Finder Quizzes",
    description:
      "Build a style quiz identifying design preferences, then provide curated product recommendations matching their aesthetic.",
    prompt: `**Build a home style finder quiz that identifies design preferences and provides curated product recommendations matching their aesthetic personality.**

**QUIZ FOCUS:** e.g., "Discover Your Home Style" OR "Find Your Design DNA" OR "What's Your Decorating Personality?"

**STYLE RANGE:** e.g., Modern, traditional, boho, industrial, coastal, farmhouse (20-40 products to match from)

**Quiz Taker Type:** Choose one:
- [ ] Style-undefined (don't know their aesthetic yet)
- [ ] Overwhelmed decorators (too many options, need direction)
- [ ] Cohesion-seekers (want consistent look)

**Tone & Style:**
- Fun design discovery energy
- Affirming and inspiring
- Personality-driven not prescriptive
- "There's no wrong answer" positivity

**Quiz Question Guidelines:** Each question should:
- Use visual style examples or mood imagery
- Balance aesthetic with lifestyle practical needs
- Build toward specific design categories
- Feel like personality reveal
- 6-8 total questions maximum

**Email Gate Strategy:**
- Place after question #4 (mid-commitment)
- Frame as "Get your personalized style guide + product picks"
- Offer incentive: "Unlock style + 20% off curated collection"
- Single field: email only
- Trust line: "Join 150,000+ who discovered their style"

**Results Page Elements:**
- **Personalized Headline:** "Your style is [Style Name] - here's your curated collection"
- **Style Identity Badge:** "You're [Modern Minimalist/Cozy Traditional/Bohemian Eclectic/etc.]"
- **Style Profile Description:** What defines this aesthetic (colors, materials, vibe, origins) (2-3 paragraphs)
- **Curated Product Collection:** 12-20 items perfectly matching their style
- **Complete Room Looks:** 2-3 fully styled spaces in their aesthetic
- **Product Details:** Why each fits their style personality
- **Product Cards Include:** Image, price, style notes, "Add to Collection"
- **Match Reasoning:** "You prefer [answer], so this [design element] embodies that"
- **Color Palette Guide:** Recommended colors for their style
- **Material & Texture:** Key finishes that define aesthetic
- **Styling Principles:** How to achieve authentic look
- **Mix & Match Guidelines:** Which pieces layer together
- **Budget Levels:** Starter collection vs. complete transformation
- **Room Priority:** Where to start (living room, bedroom, etc.)
- **Celebrity/Influencer Examples:** "Style twins" for reference
- **Real Customer Spaces:** Homes in similar aesthetic (4-6 photos)
- **Style Evolution:** How to transition if between styles
- **CTAs:** "Shop Your Style" and "Get Complete Collection"
- **Retake Option:** "Style changed? Discover again"

**Conversion Tactics Use:**
- Style personality naming and validation
- Complete aesthetic bundles
- Celebrity/influencer style comparisons
- Starter vs. full collection options
- Room-by-room roadmap
- Style mixing guidance if multi-aesthetic

**Key Quiz Questions to Include:**
- **Visual Preference Test:** Show 6 room images, pick favorites
- **Color Attraction:** Neutrals, jewel tones, earth tones, pastels, bold
- **Pattern Comfort:** Solid, subtle, bold patterns, eclectic mix
- **Material Draw:** Natural wood, metals, glass, soft textiles, mixed
- **Formality Level:** Laid-back casual, polished relaxed, formal elegant
- **Era Affinity:** Contemporary, mid-century, vintage, traditional, transitional
- **Clutter Philosophy:** Minimalist clean, curated display, collected maximalist
- **Lifestyle Needs:** Entertaining focus, family-friendly, pet considerations, work-from-home

**Goal:** Convert indecisive decorators into confident buyers by providing personalized style identity discovery with curated product recommendations that eliminate overwhelm, create aesthetic cohesion, and increase order value through complete collection bundles tailored to their design personality.`,
  },
  {
    icon: <Home />,
    title: "Gift Guide Landing Page",
    description:
      "Create a gift guide organized by price and recipient type, featuring best-sellers and registry builder tools.",
    prompt: `**Build a gift guide landing page organized by price points and recipient types to simplify gift shopping with strategic best-seller showcasing.**

**GIFT GUIDE FOCUS:** e.g., "Holiday Gift Guide" OR "Housewarming Gift Collection" OR "Wedding Gift Registry Favorites"

**ORGANIZATION:** e.g., By price range, by recipient type, by occasion, by room

**Shopper Type:** Choose one:
- [ ] Gift buyers (shopping for others)
- [ ] Registry creators (building wish lists)
- [ ] Occasion shoppers (housewarming, wedding, holiday)

**Tone & Style:**
- Helpful gift consultant
- Occasion-appropriate excitement
- Stress-reducing simplicity
- "Perfect gift guaranteed" confidence

**CTA Guidelines:** Each CTA should:
- Reference gifting ease ("Give the Perfect Gift")
- Use recipient language ("For the Host")
- Include price clarity ("Gifts Under $50")
- Emphasize popularity ("Best-Seller")

**Conversion Tactics Use:**
- Price range filtering for budget
- Recipient persona matching
- Best-seller social proof
- Gift wrap/message options
- Registry add functionality
- Complete gift bundle suggestions

**Key Elements to Include:**
- **Hero Section:** Gift guide headline, featured gift image, "Shop Gifts" CTA
- **Browse by Price:**
  - Under $25
  - $25-$50
  - $50-$100
  - $100-$200
  - $200+ (luxury gifts)
- **Browse by Recipient:**
  - For the host/entertainer
  - For the homebody
  - For newlyweds/couples
  - For new homeowners
  - For design lovers
  - For the minimalist
- **Best-Seller Section:** Top 10-15 most-gifted items with "Most Popular" badges
- **Product Cards Include:**
  - Image
  - Price
  - Recipient suitability
  - Best-seller badge if applicable
  - "Add to Registry" option
  - "Add Gift Wrap" option
  - Quick gift description
- **Complete Gift Sets:** Pre-bundled gift collections by theme with savings
- **Gift Wrap Options:** Available services with pricing
- **Gift Message:** Include personal note option
- **Registry Builder Tool:**
  - "Create Registry" CTA
  - Add items to registry from guide
  - Share registry link
  - Track purchases
  - Registry completion discount
- **Occasion-Specific Sections:** Housewarming, wedding, holiday sub-guides
- **Gift Ideas by Room:** Kitchen, bedroom, bathroom, living room
- **Last-Minute Gifts:** Quick ship, digital options, gift cards
- **Corporate/Bulk Gifting:** Volume purchase options
- **Gift Guides Downloadable:** PDF versions for sharing
- **Customer Favorites:** "What others are gifting" social proof
- **Gift Returns Policy:** Easy exchange/return for recipients
- **CTAs:** Hero, price sections, recipient categories, registry builder (numerous)
- **FAQ:** 5-7 questions (Gift wrap cost? Registry how? Returns for gifts? Ship to recipient? Message include? Bulk orders?)

**Goal:** Maximize gift purchases by eliminating decision paralysis through organized price/recipient filtering, best-seller validation, and convenient registry tools that increase average order value through gift bundles while simplifying the gifting experience with wrapping and messaging options.`,
  },
  {
    icon: <Palette />,
    title: "Collaboration Drop Page",
    description:
      "Build a limited release page featuring partner background and design inspiration, with exact drop time.",
    prompt: `**Build a limited collaboration drop page that generates hype through partner storytelling, design inspiration, and precise launch timing.**

**COLLABORATION PARTNER:** e.g., "x Designer Name" OR "x Celebrity/Influencer" OR "x Brand Partnership"

**DROP TYPE:** e.g., Limited-edition collection, exclusive colorway, signature piece, capsule collaboration

**Audience Type:** Choose one:
- [ ] Brand loyalists (excited for new releases)
- [ ] Partner's followers (crossover audience)
- [ ] Limited-edition collectors (FOMO-driven)

**Tone & Style:**
- Exclusive insider access
- Storytelling and narrative-rich
- Hype-building but authentic
- Community celebration energy

**CTA Guidelines:** Each CTA should:
- Emphasize exclusivity ("Limited Drop")
- Reference collaboration ("Get the Collab")
- Use scarcity language ("Only X Made")
- Include alert signup ("Don't Miss Launch")

**Conversion Tactics Use:**
- Exact drop date/time countdown
- Email/SMS alert signup
- Limited quantity messaging
- Behind-the-scenes design story
- Partner's credibility and following
- Pre-launch waitlist or early access

**Key Elements to Include:**
- **Hero Section:** Collaboration visual, partner logos, drop date/time, "Get Notified" CTA
- **Drop Countdown:** Large timer to exact launch (days:hours:minutes:seconds)
- **Email/SMS Alert Signup:** Capture for launch notification
- **The Collaboration Story:** How partnership formed, creative vision (3-4 paragraphs)
- **Partner Introduction:** Who they are, their design philosophy, why this partnership
- **Design Inspiration:** Mood board, sketches, inspiration behind collection
- **Behind-the-Scenes:** Development process, design decisions, material selection
- **Collection Details:** What's dropping (pieces, colorways, special features)
- **Limited Availability:** Exact quantity or "extremely limited" messaging
- **What Makes It Special:** Unique elements only in this collaboration
- **Partner Quote/Video:** Their perspective and excitement about collaboration
- **Product Previews:** Detailed looks at key pieces (5-8 hero images)
- **Styling Suggestions:** How partner envisions pieces used
- **Launch Day Details:** What happens at drop time, purchase process
- **Purchase Limits:** Per customer restrictions if applicable
- **Exclusivity Messaging:** "Never restocking" or "One-time release"
- **Early Access Tier:** VIP/subscriber first access if applicable
- **Social Proof:** Hype from partner's community or previous drops
- **CTAs:** Hero alert signup, mid-page reminders, footer countdown (4-5 total)
- **FAQ:** 5-7 questions (Exact drop time? Purchase limit? Shipping timeline? Restock? Early access?)

**Goal:** Maximize launch-day sales through strategic hype-building and alert capture that ensures maximum audience presence at drop time, converting both brand loyalists and partner's followers through exclusive collaboration storytelling, limited availability urgency, and seamless notification system.`,
  },
  {
    icon: <Gift />,
    title: "Flash Sale Landing Page",
    description:
      "Create a sale page with countdown timer, bestseller grid showing discounted prices, and urgency messaging.",
    prompt: `**Build a flash sale landing page that drives immediate purchases through countdown urgency, bestseller showcasing, and strategic discount displays.**

**SALE FOCUS:** e.g., "24-Hour Flash Sale" OR "Weekend Home Blitz" OR "48-Hour Decor Sale"

**DISCOUNT TYPE:** e.g., "Automatic 40% off at checkout" OR "Tiered discounts" OR "Select items 30-60% off"

**Shopper Type:** Choose one:
- [ ] Impulse buyers (time pressure converts)
- [ ] Deal waiters (holding out for sales)
- [ ] Room refreshers (sale triggers project start)

**Tone & Style:**
- High-energy and urgent
- Exciting value celebration
- Clear about time limitation
- No-pressure but time-conscious

**CTA Guidelines:** Each CTA should:
- Emphasize time scarcity ("Ends in X Hours")
- Reference automatic savings ("40% Off Applied")
- Use urgency language ("Shop Before It Ends")
- Include countdown context

**Conversion Tactics Use:**
- Multiple countdown timer placements
- Auto-applied discount at checkout
- Bestseller social proof
- "Selling fast" live notifications
- Category/room spotlights
- Complete room bundle suggestions

**Key Elements to Include:**
- **Hero Section:** Sale headline with discount %, large countdown timer, "Shop Now" CTA
- **Countdown Timer:** Hours:minutes:seconds (large, sticky top bar, updates live)
- **Auto-Discount Banner:** "Discount automatically applied at checkout - no code needed"
- **Sale Bestsellers Grid:** 15-30 top-selling items with discount badges
- **Filter Options:** By room, by category, by discount depth, bestsellers only
- **Product Cards:** Discount %, strikethrough price, sale price, star rating, "Selling fast" tags
- **Room/Category Spotlights:** Featured sections (bedroom, living, office) with sale items
- **Urgency Indicators:** "X sold in last hour" or "Low stock" badges
- **Social Proof Ticker:** Live purchase notifications ("Sarah just bought this")
- **Sale Countdown Reminders:** Repeated timers mid-page and footer
- **Free Shipping Threshold:** Progress bar with suggested items
- **Bundle Suggestions:** "Complete your room" with sale items
- **Sale Terms:** Clear end time, exclusions if any, return policy
- **Limited Inventory Alerts:** Stock counters on popular items
- **Price Comparison:** Regular vs. flash sale savings highlighted
- **CTAs:** Hero, category sections, sticky "Shop Sale" bar (always visible)
- **FAQ:** 3-4 questions (When ends? Automatic discount? Returns? Stack discounts?)

**Goal:** Maximize sales velocity during limited window by creating urgency through countdown timers and bestseller showcasing while ensuring frictionless shopping with automatic discount application and clear sale mechanics that convert hesitant browsers into immediate buyers driven by FOMO.`,
  },
];
